import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import Lottie from 'react-lottie-player';
import { Alert, CardBody, Col, Container, Form, FormFeedback, Input, Label, Row, Popover, PopoverBody } from "reactstrap";
import Card from "components/PurpleTheme/Card";
import { googleLogout, useGoogleLogin } from '@react-oauth/google';
import googleIcon from '../../assets/images/google.svg';
import oktaIcon from '../../assets/images/Okta_logo.svg';

//redux
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import withRouter from "components/Common/withRouter";

// Formik validation
import * as Yup from "yup";
import {isString, useFormik} from "formik";

// actions
import {
  loginUser,
  googleSsoLogin,
  googleSsoSetCookie,
  postSsoOnlyLoginStatus,
  oktaSsoLogin,
  initiateOktaLoginFlow
} from "../../store/actions";

// import images
import refreshResourcesLottie from '../../assets/images/refresh_resources_lottie.json';

import { useLocation } from 'react-router-dom';
import log from "echarts/src/scale/Log";
import {showNotification} from "../../components/Common/notification";

const Login = props => {

  //meta title
  document.title = "Login | Temperstack";

  const [domainName, setDomainName] = useState("");
  const [hover, setHover] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [twoFactorAuthEnabled, setTwoFactorAuthEnabled] = useState(false);
  const [subdomainSsoLoginStatus, setSubdomainSsoLoginStatus] = useState(false);
  const [otp, setOtp] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const sso_available_domains = []; //"haarvishtest", "10xenggnew", "rehaan"];

  const dispatch = useDispatch();

  const { ssoLoginStatus } = useSelector(state => ({
    ssoLoginStatus: state.Login.subdomain_sso_only_login,
  }));

  const {login_details} = useSelector(state => ({
    login_details: state.Login.login_details,
  }));


  const handleChangeOtp = (e) => {
    const inputValue = e.target.value;

    if (!/^\d*$/.test(inputValue)) {
      setErrorMessage('Please enter numeric values only');
      return;
    }

    if (inputValue.length > 6) {
      setErrorMessage('You can only enter up to 6 digits');
      return;
    }

    setErrorMessage('');
    setOtp(inputValue);
  }


  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && otp.length === 6) {
      handleSubmit();
    }
  }

  const handleSubmit = () => {
    const data = {
      subdomain_name: domainName,
      email: validation.values.email,
      password: validation.values.password,
      otp_for_verification: otp
    }
    dispatch(loginUser(data, props.router.navigate));
  }

  useEffect(() => {
    let status = false

    if("sso_only_login" in ssoLoginStatus)
      status = ssoLoginStatus.sso_only_login;

    setSubdomainSsoLoginStatus(status);
  }, [ssoLoginStatus])

  useEffect(() => {
    let data = {
        subdomain: subdomain
    }

    if(!isCommonLoginHost)
      dispatch(postSsoOnlyLoginStatus(data));
  },[postSsoOnlyLoginStatus])


  useEffect(() => {
    setTwoFactorAuthEnabled(login_details?.is_two_factor_auth_enabled || false)
    if (login_details?.otp_verified === false) {
      setErrorMessage(login_details?.message || '')
    }
    if (login_details?.can_request_otp===false){
      setErrorMessage(login_details?.message || '')
    }
    if(login_details?.message_sent === true){
      showNotification('success',login_details?.message)
      setIsButtonDisabled(true);
      setCountdown(60);
    }
  }, [login_details]);

  useEffect(() => {
    const array = window.location.href.split('/');
    const url_split = array[2].split(".");
    const subdomain = url_split[0].toLowerCase();
    if (url_split.length === 3 && subdomain !== "www" && subdomain !== "api" && subdomain !== "app" && subdomain !== "dev" && subdomain !== "staging" && subdomain !== "uat" && subdomain !== "docs") {
      setDomainName(url_split[0]);
    }
  }, []);

  const hostname = window.location.hostname;
  // get the domain
  const subdomain = hostname.split('.')[0];
  const domain = hostname.split('.').slice(1).join('.');
  const protocol = window.location.protocol;

  const isCommonLoginHost = subdomain === 'app';
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  // Read query parameters token, is_cloud_onboarded, is_service_onboarded, fyno_signature, user_id and set localStorage
  const token = searchParams.get('token');
  const is_cloud_onboarded = searchParams.get('is_cloud_onboarded');
  const is_service_onboarded = searchParams.get('is_service_onboarded');
  const fyno_signature = searchParams.get('fyno_signature');
  const user_id = searchParams.get('user_id');
  const next = searchParams.get('next');
  const access_token = searchParams.get('access_token');
  const refresh_token = searchParams.get('refresh_token');
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const redirectUri = window.location.origin + '/login';

  useEffect(() => {
    if (code && state && redirectUri) {
      const payload = {
        code: code,
        state: state,
        redirect_uri: redirectUri
      };
      dispatch(initiateOktaLoginFlow(payload));
    }
  }, [code, state, redirectUri]);

  if (user_id) {

    localStorage.setItem("authUser", token);
    localStorage.setItem("isCloudLoaded", is_cloud_onboarded);
    localStorage.setItem("isServiceLoaded", is_service_onboarded);
    localStorage.setItem("fyno_signature", fyno_signature);
    localStorage.setItem("user_id", user_id);
  }

  useEffect(() => {
    //dispatch a server request to set cookies
    let data = {
      access_token: access_token,
      refresh_token: refresh_token,
      domain: hostname
    }

    if (next) {
      data.next_url = next;
    }

    if (access_token && refresh_token)
    {
      dispatch(googleSsoSetCookie(data));
    }

  }, [access_token, refresh_token]);

  const validation = useFormik({
    // enableReinitialize : use this  flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      subdomain_name: domainName
    },
    validationSchema: Yup.object({
      subdomain_name: Yup.string().required("Please Enter Your Subdomain name"),
      email: isCommonLoginHost ? Yup.string() : Yup.string().required("Please Enter Your Email"),
      password: isCommonLoginHost ? Yup.string() : Yup.string().required("Please Enter Your Password"),
    }),

    onSubmit: (values) => {
      if (next) {
        values.next = next;
      }
      if (isCommonLoginHost) {
        const domain = hostname.split('.').slice(1).join('.');
        const currentUrl = new URL(window.location.href);
        const params = new URLSearchParams(currentUrl.search);
        const newUrl = new URL(`http://${values.subdomain_name}.${domain}/login`);
        newUrl.search = params.toString();
        window.location.href = newUrl.href;
      } else {
        dispatch(loginUser(values, props.router.navigate));
      }
    }
  });

  const { error } = useSelector(state => ({
    error: state.Login.error,
  }));


  const [passwordShown, setPasswordShown] = useState(false);

  const togglePassword = () => {
    setPasswordShown(!passwordShown);
  };

  const handleSendOtp = () => {
    const data = {
      subdomain_name: domainName,
      email: validation.values.email,
      password: validation.values.password,
      request_otp: true,
    };
    dispatch(loginUser(data, props.router.navigate));
    setIsButtonDisabled(true);
    setCountdown(60); // Set countdown to 60 seconds
  };

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prevCountdown) => prevCountdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      setIsButtonDisabled(false);
      clearInterval(timer);
    }
    return () => clearInterval(timer);
  }, [countdown]);



  const googleLogin = useGoogleLogin({
    onSuccess: (codeResponse) => {
      let google_sso_data = {
        access_token: codeResponse.access_token,
        subdomain_name: subdomain,
        next_url: next
      }

      dispatch(googleSsoLogin(google_sso_data));
    },
    onError: (error) => console.log('Login Failed:', error)
  });

  function handleClickOktaLogin() {
    dispatch(oktaSsoLogin())
  }

  return (
    <React.Fragment>
      <div className="my-3 pt-sm-3">
        <Container fluid>
          <Row className="justify-content-center why-bluejay">
            <Col lg={8} className="mt-4">
              
                <CardBody className="pt-0">
                  <Row>
                    <h4 className="text-white mt-4 why-bluejay-font">Never miss an Alert with Temperstack!</h4>
                    <Col lg="6" className="">
                      <h5 className="why-bluejay no-background">
                        Log in to alerting awesomeness:</h5>
                      <ul className="mt-4">
                        <li className="mt-2 text-white">Out-of-box Cross-platform Integrations</li>
                        <li className="mt-2 text-white">Automated Continuous Audit</li>
                        <li className="mt-2 text-white">Automated Alerts setup</li>
                      </ul>
                      {/* <h5 className="text-muted why-bluejay">See Bluejay in action</h5> */}
                    </Col>
                    <Col lg="4" className="scan-animation">
                      <Lottie
                        loop
                        animationData={refreshResourcesLottie}
                        play
                      //style={{ width: 250, height: 250 }}
                      />
                    </Col>
                  </Row>
                </CardBody>
            </Col>
            <Col lg={4} className="mt-4">
              <Card className="overflow-hidden mt-4">
                <CardBody className="">
                  <h3 className="">Log In</h3>
                  <div className="p-2">
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      {error ? <Alert color="danger">{error}</Alert> : null}

                      <div className="mb-3">
                        <Label className="form-label">Subdomain</Label>
                        {isCommonLoginHost ? (<div className="input-org ">
                          <Input
                            name="subdomain_name"
                            type="text"
                            className="input-no-border bg-transparent"
                            placeholder="Organization"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.subdomain_name || ""}
                            invalid={
                              validation.touched.subdomain_name && validation.errors.subdomain_name ? true : false
                            }
                            readOnly={!isCommonLoginHost}
                            disabled={!isCommonLoginHost}
                          />
                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <span className="subdomain-label border-start">{'.' + domain}</span>
                            {!isCommonLoginHost && (
                              <>
                                <i id="common-login-redirect" className="mdi mdi-pencil mdi-18px me-2"
                                  onMouseEnter={
                                    () => {
                                      setPopoverOpen(true);
                                    }
                                  }
                                  onMouseLeave={
                                    () => {
                                      setPopoverOpen(false);
                                    }
                                  }
                                  onClick={() => window.location.href = protocol + '//app.' + domain + '/login'}
                                />
                                <Popover
                                  placement="top"
                                  isOpen={popoverOpen}
                                  target="common-login-redirect"
                                  toggle={() => setPopoverOpen(!popoverOpen)}
                                >
                                  <PopoverBody>Change Organization</PopoverBody>
                                </Popover>
                              </>
                            )}
                          </div>
                        </div>) : (
                          <div className="input-org" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span className="subdomain-label border-start">{validation.values.subdomain_name + '.' + domain}</span>
                            {!isCommonLoginHost && (
                              <>
                                <i id="common-login-redirect" className="me-2 mdi mdi-pencil mdi-18px"
                                  onMouseEnter={
                                    () => {
                                      setPopoverOpen(true);
                                    }
                                  }
                                  onMouseLeave={
                                    () => {
                                      setPopoverOpen(false);
                                    }
                                  }
                                  onClick={() => window.location.href = protocol + '//app.' + domain + '/login'}
                                />
                                <Popover
                                  placement="top"
                                  isOpen={popoverOpen}
                                  target="common-login-redirect"
                                  toggle={() => setPopoverOpen(!popoverOpen)}
                                >
                                  <PopoverBody>Change Organization</PopoverBody>
                                </Popover>
                              </>
                            )}
                          </div>
                        )}
                        <div className="invalid-error">{validation.errors.subdomain_name}</div>
                      </div>

                      {!isCommonLoginHost && !subdomainSsoLoginStatus && (
                        <>
                          <div className="mb-3">
                            <Label className="form-label">Email</Label>
                            <Input
                              name="email"
                              className="input"
                              placeholder="Enter email"
                              type="email"
                              disabled={twoFactorAuthEnabled}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.email || ""}
                              invalid={
                                validation.touched.email && validation.errors.email ? true : false
                              }
                            />
                            {validation.touched.email && validation.errors.email ? (
                              <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                            ) : null}
                          </div>
                          <div className="mb-3">
                            <Label className="form-label">Password</Label>
                            <div className="input-org bg-transparent">
                              <Input
                                name="password"
                                className="bg-transparent"
                                type={passwordShown ? "text" : "password"}
                                disabled={twoFactorAuthEnabled}
                                placeholder="Enter Password"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.password || ""}
                                invalid={
                                  validation.touched.password && validation.errors.password ? true : false
                                }
                              />
                              {
                                passwordShown ?
                                  <i className="password-label fa fa-eye-slash me-2 input-label"
                                    onClick={togglePassword}></i>
                                  :
                                  <i className="password-label fa fa-eye me-2 input-label" onClick={togglePassword}></i>
                              }
                            </div>
                            <div className="invalid-error">{validation.errors.password}</div>
                          </div>
                        </>
                      )}

                      <div className="mt-4 text-center">
                        {isCommonLoginHost ? (
                          <button
                            type="submit"
                            style={{ background: 'none', border: 'none' }}
                            onMouseEnter={() => setHover(true)}
                            onMouseLeave={() => setHover(false)}
                          >
                            <i className="mdi mdi-arrow-right-circle mdi-36px" style={{ color: hover ? "#485ec4" : "#556ee6" }} />
                          </button>
                        ) : (
                            <>
                              {!subdomainSsoLoginStatus && (
                                  <button
                                      className="btn btn-primary w-75"
                                      type="submit"
                                      disabled={twoFactorAuthEnabled}
                                  >
                                    Log In
                                  </button>
                              )}
                              <div>
                                {twoFactorAuthEnabled? (
                                    <div className='mt-4'>
                                      <div className='d-flex justify-content-start'>
                                        <Label className="form-label">Enter OTP</Label>
                                      </div>
                                      <div>
                                        <Input
                                            name='otp'
                                            type="text"
                                            placeholder="Enter OTP"
                                            className='form-control'
                                            onChange={handleChangeOtp}
                                            onKeyPress={handleKeyPress}
                                            value={otp}
                                        />
                                        {errorMessage && <div className="text-danger">{errorMessage}</div>}
                                      </div>
                                      <div className='d-flex justify-content-center gap-2 mt-3'>
                                        <div>
                                          <button
                                              type='button'
                                              className='btn btn-secondary'
                                              onClick={handleSendOtp}
                                              disabled={isButtonDisabled}
                                          >
                                            {isButtonDisabled ? `Resend OTP in ${countdown}s` : 'Resend OTP'}
                                          </button>
                                        </div>
                                        <button
                                            type='button'
                                            className="btn btn-primary"
                                            onClick={handleSubmit}
                                            disabled={otp.length !== 6}
                                        >
                                          Verify OTP
                                        </button>
                                      </div>
                                    </div>

                                ) : null}
                              </div>
                              {/*{(sso_available_domains.includes(subdomain) && !subdomainSsoLoginStatus) &&*/}
                              {(!subdomainSsoLoginStatus) &&
                                  <div className="d-flex-column w-100 justify-content-center mt-2">
                                  <div className="d-flex justify-content-center align-items-center w-75 m-auto">
                                   <hr className="flex-grow-1"/>
                                   <p className="px-2 m-0">or</p>
                                   <hr className="flex-grow-1"/>
                               </div>
                             </div>
                              }
                              <div style={{
                                paddingTop: '10px'
                              }}>
                                {/*{!sso_available_domains.includes(subdomain) &&*/}
                                {
                                  <button className="w-md w-75 btn btn-outline-light"
                                          style={{fontWeight:"500", border:"1px solid #7481AF"}}
                                          onClick={() => {
                                            window.location.href = protocol + "//app." + domain + "/identity?client_subdomain=" + subdomain;
                                          }}>
                                    <div className="d-flex align-items-center justify-content-center">
                                      <img src={googleIcon} style={{height: "24px", color:"secondary"}}
                                           className="bx bxl-google me-2"/>Sign in with Google
                                    </div>
                                  </button>
                                }
                              </div>
                              <div style={{paddingTop: '10px'}}>
                                  <button className="w-md w-75 btn btn-outline-light"
                                          type={'button'}
                                          style={{fontWeight:"500", border:"1px solid #7481AF"}}
                                          onClick={() => {handleClickOktaLogin()}}>
                                    <div className="d-flex align-items-center justify-content-center">
                                      <img src={oktaIcon} style={{height: "24px", color:"secondary"}}
                                           className="bx bxl-google me-2"/>Sign in with Okta
                                    </div>
                                  </button>
                              </div>
                            </>
                        )}
                      </div>


                      <div className="mt-4 text-center">
                        <Link to="/forgot-password" className="">
                        <i className="mdi mdi-lock me-1" />
                          Forgot your password?
                        </Link>
                      </div>
                      <div className="mt-2 text-center">
                        <Link to="/register" className="text-end">
                          <i className="mdi mdi-account me-1" />
                          Register Now
                        </Link>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col lg={12}>

            </Col>
          </Row>
        </Container>
      </div >
    </React.Fragment >
  );
};

export default withRouter(Login);

Login.propTypes = {
  history: PropTypes.object,
};
